// Встроенная справка: пошаговые инструкции по каждому разделу. Открывается из меню «Справка»
// и кнопкой «?» в шапке — сразу на разделе текущей страницы.
import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { Alert, Badge, Grid, Group, List, Paper, Stack, Text, TextInput, Title, UnstyledButton } from '@mantine/core';
import { HELP, type HelpSection } from '../help/helpContent.ts';

/** Раздел справки для адреса страницы — по самому длинному совпадающему началу адреса */
export function helpFor(pathname: string): HelpSection | undefined {
  let best: HelpSection | undefined;
  let len = 0;
  for (const s of HELP) for (const r of s.routes) {
    if ((pathname === r || pathname.startsWith(r + '/') || pathname.startsWith(r + '?')) && r.length > len) { best = s; len = r.length; }
  }
  return best;
}

const norm = (s: string) => s.toLowerCase().replace(/ё/g, 'е');

export function HelpPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const current = HELP.find((s) => s.id === id) ?? HELP[0];
  const found = useMemo(() => {
    const t = norm(q.trim());
    if (!t) return HELP;
    return HELP.filter((s) => norm([s.title, s.summary, ...s.steps.map((x) => x.title + ' ' + x.text), ...(s.tips ?? [])].join(' ')).includes(t));
  }, [q]);
  const groups = [...new Set(found.map((s) => s.group))];
  const idx = HELP.indexOf(current);

  return (
    <Grid gap="md">
      <Grid.Col span={{ base: 12, md: 3 }}>
        <Paper withBorder p="sm" className="help-toc">
          <Alert color="blue" p="xs" mb="sm"><Link to="/manual">📘 Полная инструкция менеджера со скриншотами</Link></Alert>
          <TextInput size="sm" placeholder="Поиск по справке" value={q} onChange={(e) => setQ(e.currentTarget.value)} mb="sm" />
          {groups.map((g) => (
            <Stack key={g} gap={0} mb="xs">
              <Text size="xs" tt="uppercase" fw={700} c="dimmed" mb={2}>{g}</Text>
              {found.filter((s) => s.group === g).map((s) => (
                <UnstyledButton key={s.id} className="help-toc-item" data-active={s.id === current.id || undefined} onClick={() => navigate(`/help/${s.id}`)}>
                  {s.title}
                </UnstyledButton>
              ))}
            </Stack>
          ))}
          {!found.length && <Text size="sm" c="dimmed">Ничего не найдено</Text>}
        </Paper>
      </Grid.Col>
      <Grid.Col span={{ base: 12, md: 9 }}>
        <Stack gap="sm">
          <Group justify="space-between" align="flex-start">
            <div>
              <Text size="xs" c="dimmed">{current.group}</Text>
              <Title order={2}>{current.title}</Title>
            </div>
            {current.routes[0] && current.routes[0] !== '/login' && (
              <Link to={current.routes[0]} className="help-open">Открыть экран →</Link>
            )}
          </Group>
          <Group gap={6}><Badge variant="light" color="gray">кто работает: {current.roles}</Badge></Group>
          <Text>{current.summary}</Text><Text size="xs" c="dimmed">Изменённые процессы актуализированы 03.11.2026. Новые снимки — из учебной копии текущего интерфейса; данные на них приведены для примера.</Text>
          <Paper withBorder p="md">
            <List type="ordered" spacing="md" className="help-steps">
              {current.steps.map((s, k) => (
                <List.Item key={k}>
                  <Text fw={600}>{s.title}</Text>
                  <Text size="sm">{s.text}</Text>
                  {s.image && <figure style={{margin:'12px 0 0'}}><a href={s.image} target="_blank" rel="noreferrer"><img src={s.image} alt={s.imageAlt ?? s.title} loading="lazy" style={{display:'block',width:'100%',maxWidth:900,height:'auto',border:'1px solid #dbe3ef',borderRadius:8}} /></a><figcaption style={{fontSize:12,color:'#64748b',marginTop:6}}>{s.imageAlt ?? s.title}. Нажмите, чтобы увеличить.</figcaption></figure>}
                </List.Item>
              ))}
            </List>
          </Paper>
          {!!current.tips?.length && (
            <Alert color="yellow" title="Важно">
              <List spacing={4} size="sm">{current.tips.map((t, k) => <List.Item key={k}>{t}</List.Item>)}</List>
            </Alert>
          )}
          <Group justify="space-between">
            {idx > 0 ? <UnstyledButton className="help-open" onClick={() => navigate(`/help/${HELP[idx - 1].id}`)}>← {HELP[idx - 1].title}</UnstyledButton> : <span />}
            {idx < HELP.length - 1 && <UnstyledButton className="help-open" onClick={() => navigate(`/help/${HELP[idx + 1].id}`)}>{HELP[idx + 1].title} →</UnstyledButton>}
          </Group>
        </Stack>
      </Grid.Col>
    </Grid>
  );
}
